import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Wind, Heart, Brain, Users, Sparkles, Leaf } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import ServiceCard from '../components/ServiceCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand images - User provided
const HERO_BG = "/img/hero-tree-lg.webp";
const CAVE_SHOWER = "https://i.postimg.cc/KjkdFfGj/Whats-App-Image-2026-02-28-at-20-42-21.jpg";
const SALT_CAVE = "/img/canyon-lg.webp";
const TREE_IMG = "/img/tree-sun-lg.webp";
const CANYON_IMG = "/img/canyon-lg.webp";
const WHITE_BG = "/img/wall-bg-lg.webp";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.15 } }
};

export const HomePage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, contentRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/content`)
        ]);
        setServices(servicesRes.data.slice(0, 3));
        
        // Organizar contenido por sección
        const contentMap = {};
        contentRes.data.forEach(item => {
          if (item.page === 'home') {
            contentMap[item.section] = item;
          }
        });
        setContent(contentMap);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section - Clean & Elegant */}
      <section className="relative min-h-[80vh] md:min-h-[85vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="HALO Cueva de Sal"
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-halo-charcoal/60 via-halo-charcoal/20 to-halo-charcoal/40" />
        </div>

        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-24">
            <motion.div 
              className="flex flex-col items-center text-center"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              {/* Logo texto elegante */}
              <motion.div variants={fadeInUp} className="mb-6 md:mb-10">
                <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-white tracking-wide drop-shadow-2xl">
                  <span className="italic">Halo</span>
                </h1>
                <p className="text-white/90 text-sm md:text-xl tracking-[0.3em] mt-2 font-light uppercase">
                  Cuevas de Sal
                </p>
              </motion.div>
              
              <motion.p 
                variants={fadeInUp}
                className="text-white/90 text-base md:text-lg lg:text-xl leading-relaxed mb-8 md:mb-12 max-w-2xl font-light tracking-wide"
              >
                Un espacio de bienestar donde la haloterapia se integra con prácticas 
                conscientes para el cuidado respiratorio y la regulación del sistema nervioso.
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/bonos"
                  className="inline-flex items-center justify-center gap-2 bg-halo-brown text-halo-cream px-8 py-4 rounded-full font-medium hover:bg-halo-charcoal transition-all duration-300 shadow-lg hover:shadow-xl"
                  data-testid="hero-reservar-btn"
                >
                  Comprar bono
                </Link>
                <Link
                  to="/haloterapia"
                  className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-medium hover:bg-white/30 transition-all duration-300 border border-white/30"
                  data-testid="hero-descubrir-btn"
                >
                  Descubrir Más
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Strip */}
      <section className="bg-halo-surface py-6 md:py-8 border-y border-halo-taupe/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { icon: Wind, text: 'Sistema respiratorio' },
              { icon: Sparkles, text: 'Piel y cutáneo' },
              { icon: Brain, text: 'Sistema nervioso' },
              { icon: Heart, text: 'Bienestar integral' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 md:gap-3 justify-center"
              >
                <item.icon className="text-halo-brown flex-shrink-0" size={20} />
                <span className="text-halo-charcoal text-xs md:text-sm font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Clean */}
      <section className="py-16 md:py-24 bg-halo-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6 order-2 lg:order-1"
            >
              <span className="text-halo-brown font-medium tracking-widest text-xs md:text-sm">
                SOBRE NOSOTROS
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-halo-charcoal leading-tight">
                Un espacio para <br className="hidden md:block" />
                <span className="text-halo-brown italic">detenerse y respirar</span>
              </h2>
              <p className="text-halo-text leading-relaxed text-base md:text-lg">
                En HALO creemos que el bienestar no es un lujo, sino una necesidad esencial 
                en una sociedad que vive cada vez más deprisa.
              </p>
              <p className="text-halo-text leading-relaxed text-sm md:text-base">
                La haloterapia actúa como un apoyo natural en el bienestar respiratorio, 
                favoreciendo la limpieza de las vías respiratorias y reduciendo la 
                inflamación.
              </p>
              <Link
                to="/filosofia"
                className="inline-flex items-center gap-2 text-halo-brown font-medium hover:gap-3 transition-all text-sm md:text-base"
                data-testid="home-filosofia-link"
              >
                Conoce nuestra filosofía
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={content.about?.image_url || CAVE_SHOWER}
                  alt="HALO experiencia"
                  className="w-full h-[350px] md:h-[450px] lg:h-[500px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 md:py-24 bg-halo-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-halo-brown font-medium tracking-widest text-xs md:text-sm">
              NUESTROS SERVICIOS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-halo-charcoal mt-4 mb-4 md:mb-6">
              Experiencias de bienestar
            </h2>
            <p className="text-halo-text max-w-2xl mx-auto text-sm md:text-base">
              Cada actividad se diseña para potenciar los efectos de la haloterapia.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-80 md:h-96 skeleton rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {services.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} />
              ))}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10 md:mt-12"
          >
            <Link
              to="/servicios"
              className="inline-flex items-center gap-2 bg-halo-charcoal text-halo-cream px-6 md:px-8 py-3 md:py-4 rounded-full font-medium text-sm md:text-base hover:bg-halo-brown transition-all duration-300"
              data-testid="home-all-services-btn"
            >
              Ver Todos los Servicios
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Families Section - Simplified */}
      <section className="py-16 md:py-24 bg-halo-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <img
                src={CANYON_IMG}
                alt="Ambiente relajante"
                className="rounded-2xl w-full h-[300px] md:h-[400px] object-cover shadow-xl"
                loading="lazy"
                decoding="async"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-halo-sage/20 text-halo-charcoal px-4 py-2 rounded-full">
                <Users size={16} className="text-halo-sage" />
                <span className="text-xs md:text-sm font-medium">Sesiones Familiares</span>
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl text-halo-charcoal">
                Respirar juntos, <br className="hidden md:block" />
                <span className="text-halo-brown italic">reconectar</span>
              </h2>
              
              <p className="text-halo-text leading-relaxed text-sm md:text-base">
                Una invitación a detener el ritmo, apagar la digitalización constante y 
                recuperar el tiempo de calidad entre adultos y niños.
              </p>
              
              <ul className="space-y-2 md:space-y-3">
                {[
                  'Sin pantallas ni estímulos invasivos',
                  'Zona de juego tranquilo y creativo',
                  'Beneficios respiratorios para toda la familia'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-halo-text text-sm">
                    <Leaf size={14} className="text-halo-sage flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link
                to="/familias"
                className="inline-flex items-center gap-2 text-halo-brown font-medium hover:gap-3 transition-all text-sm md:text-base"
                data-testid="home-familias-link"
              >
                Descubre las sesiones familiares
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Elegant */}
      <section className="py-16 md:py-24 bg-halo-charcoal relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={TREE_IMG}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6 md:space-y-8"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-halo-cream">
              Comienza tu camino hacia el bienestar
            </h2>
            <p className="text-halo-cream/70 text-sm md:text-lg max-w-xl mx-auto">
              Reserva tu primera sesión y descubre un espacio donde el tiempo 
              se detiene.
            </p>
            <Link
              to="/bonos"
              className="inline-flex items-center gap-2 bg-halo-brown text-halo-cream px-8 md:px-10 py-3 md:py-4 rounded-full font-medium text-base md:text-lg hover:bg-halo-charcoal transition-colors"
              data-testid="home-cta-reservar"
            >
              Comprar bono
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
